import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { Col, Row } from "reactstrap";
import { CoreGoogleDevice } from "../../models/CoreDeviceModels";
import { RequestState } from "../../models/RequestState";
import { RootState } from "../../store/store";
import TableHorizontal, { InfoObject } from "../misc/TableHorizontal";
import StatusCode from "../../util/StatusCode";
import { ListGoogleDevices } from "../../store/DeviceAction";
import { getGoogleDeviceFilters } from "../../store/FilterAction";
import { PageResult, CoreGoogleDeviceFilter } from "../../models/CoreModels";
import RequestParser from "../../util/RequestParser";
import { Link, useSearchParams } from "react-router-dom";
import FilterBottomComponent from "../misc/FilterBottomComponent";
import Search from "../misc/Search";
import MultiSelectInput from "../misc/MultiSelectInput";

const Devices = () => {
    const { t } = useTranslation(['device']);
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const devices = useSelector<RootState, RequestState<PageResult<CoreGoogleDevice>>>(state => state.device.googleDevices);
    const googleDeviceFilters = useSelector<RootState, CoreGoogleDeviceFilter>(state => state.filter.googleDeviceFilters ?? { orgUnitPaths: [], statuses: [] });
    const filter = RequestParser.parseDeviceSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        getGoogleDeviceFilters()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        ListGoogleDevices(filter)(dispatch);
        window.scrollTo(0, 0);
    }, [dispatch, searchParams]);

    const updateFilter = (name: string, value?: number | string | boolean | null | number[] | string[]) => {
        if (name !== 'page') {
            searchParams.set('page', '1');
        }

        if (typeof (value) === 'object') {
            searchParams.delete(name);
            if (value !== null && value.length > 0) {
                value.forEach(x => {
                    searchParams.append(name, x.toString())
                })
            }
        } else if (value !== undefined && value !== '' && value !== null) {        
            searchParams.set(name, value.toString());
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const debouncedSearch = useCallback(
        debounce(search => {
            updateFilter('search', search);
        }, 300), [searchParams]);

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        const search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const mapDevices = (): InfoObject[] => {
        if (devices.code === StatusCode.COMPLETE) {
            return devices.data.values.map(x => ({
                id: x.deviceId,
                data: [
                    { value: <Link to={`/device/${x.id}/information`}>{x.serialNumber}</Link> },
                    { value: t(`googleDeviceListing.status.${ x.status }`) },
                    { value: x.model },
                    { value: x.osVersion },
                    { value: x.recentUser && x.recentUser.emailAddress },
                    { value: x.orgUnitPath },
                ]
            }))
        } else {
            return [];
        }
    }

    const orgUnitPathFilterItems = googleDeviceFilters.orgUnitPaths
        .map(x => ({ value: x, label: x }));

    const statusFilterItems = googleDeviceFilters.statuses
        .map(x => ({ value: x, label: t(`googleDeviceListing.status.${x}`) }));

    const renderFilter = () => (<>
        <Row>
            <Col sm="4">
                <Search
                    value={search}
                    onChange={onSearch}
                    placeholder={t('googleDeviceListing.searchPlaceholder')}
                />
            </Col>
            <Col sm="4">
                <MultiSelectInput
                    items={orgUnitPathFilterItems}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='orgUnitPath'
                    selected={filter.orgUnitPath}
                    defaultText={t('googleDeviceListing.filter.orgUnitPath')}
                />
            </Col>
            <Col sm="4">
                <MultiSelectInput
                    items={statusFilterItems}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='status'
                    selected={filter.status}
                    defaultText={t('googleDeviceListing.filter.status')}
                />
            </Col>
        </Row>
    </>);

    const renderDevices = () => (
        <TableHorizontal
            header={[
                { value: t('googleDeviceListing.tableHeaders.serialNumber') },
                { value: t('googleDeviceListing.tableHeaders.status') },
                { value: t('googleDeviceListing.tableHeaders.model') },
                { value: t('googleDeviceListing.tableHeaders.osVersion') },
                { value: t('googleDeviceListing.tableHeaders.recentUsers') },
                { value: t('googleDeviceListing.tableHeaders.orgUnitPath') },
            ]}

            info={mapDevices()}
            error={devices.code === StatusCode.ERROR}
            searching={devices.code === StatusCode.PENDING}
            noResMsg={t('googleDeviceListing.notFound')}
        />
    )

    return (
        <div className="container">

            <div className="header--fixed large-filter">
                <h1>{t('googleDeviceListing.title')}</h1>
                {renderFilter()}
            </div>

            <div className="body--scroll padding-40">
                {renderDevices()}
            </div>

            <FilterBottomComponent
                requestState={devices}
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
            />
        </div>
    )
}

export default Devices;
